import { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useRelativeTime from '../hooks/useRelativeTime';
import { fitAxis } from '../load_test_components/graphs/axisScale';
import { LVDT_COLORS } from '../load_test_components/graphs/LvdtGraph';

// Data Stream tab: the newest readings of one http logger, pushed over the
// page's WebSocket (useLoggerSocket). Raw logger values (digits, mA, mV) as
// they arrive, so it works for any logger whether or not it's on a box.

const LVDT_KEYS = ['LVDT1', 'LVDT2', 'LVDT3', 'LVDT4'];
const TABLE_ROWS = 10;
const CHART_HEIGHT = 170;

const fmt = (v, digits = 1) => {
  if (v === null || v === undefined) return '—';
  const text = Number(v).toFixed(digits);
  return Number(text) === 0 ? (0).toFixed(digits) : text; // no "-0.00"
};
// Logger timestamps carry no zone; parsing and printing both in local time
// shows the logger's own clock.
const clock = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

// v10.28+ programs log a 4-20 mA pressure transducer; older ones VW digits.
function pressureOf(row) {
  if (row.PressureMA !== null && row.PressureMA !== undefined) return { value: row.PressureMA, unit: 'mA' };
  return { value: row.Pressure, unit: 'digits' };
}

function ConnectionBadge({ connection }) {
  const theme = useTheme();
  const look = {
    live: { color: theme.palette.success.main, label: 'Live' },
    connecting: { color: theme.palette.warning.main, label: 'Connecting…' },
    unavailable: { color: theme.palette.error.main, label: 'Live updates unavailable, retrying' },
  }[connection] || { color: theme.palette.text.disabled, label: connection };
  return (
    <Stack direction='row' spacing={0.75} alignItems='center'>
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: look.color }} />
      <Typography variant='body2' color='text.secondary'>{look.label}</Typography>
    </Stack>
  );
}

function Stat({ label, value, unit }) {
  return (
    <Paper variant='outlined' sx={{ px: 1.5, py: 1, minWidth: 0 }}>
      <Typography variant='caption' color='text.secondary' noWrap component='div'>{label}</Typography>
      <Typography variant='subtitle1' fontWeight={700} noWrap>
        {value}
        {unit && value !== '—' && (
          <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>{unit}</Typography>
        )}
      </Typography>
    </Paper>
  );
}

function StreamChart({ title, unit, points, series }) {
  const theme = useTheme();
  const axis = useMemo(
    () => fitAxis(points.flatMap((p) => series.map((s) => p[s.key]))),
    [points, series],
  );
  if (!axis) return null;
  return (
    <Box>
      <Typography variant='body2' fontWeight={600} sx={{ mb: 0.5 }}>
        {title} <Typography component='span' variant='caption' color='text.secondary'>({unit})</Typography>
      </Typography>
      <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
        <LineChart data={points} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray='3 3' stroke={theme.palette.divider} />
          <XAxis
            dataKey='t'
            type='number'
            domain={['dataMin', 'dataMax']}
            tickFormatter={clock}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            minTickGap={40}
          />
          <YAxis
            domain={axis.domain}
            ticks={axis.ticks}
            tickFormatter={axis.format}
            width={56}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          />
          <Tooltip
            labelFormatter={clock}
            formatter={(v, name) => [v === null ? '—' : Number(v).toFixed(axis.decimals + 1), name]}
            contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
          />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {series.map((s) => (
            <Line
              key={s.key}
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              dot={false}
              strokeWidth={1.75}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

function DataStreamPanel({ logger, stream, connection }) {
  const theme = useTheme();
  const rows = stream?.loggerId === logger?.id ? stream.rows : [];
  const loaded = stream?.loggerId === logger?.id && stream.loaded;
  const newest = rows[rows.length - 1];
  const lastReceived = useRelativeTime(newest?.ReceivedAt || logger?.last_post_at);

  const points = useMemo(() => rows.map((r) => ({
    t: Date.parse(r.Timestamp),
    load: r.Loadcell,
    pressure: pressureOf(r).value,
    LVDT1: r.LVDT1,
    LVDT2: r.LVDT2,
    LVDT3: r.LVDT3,
    LVDT4: r.LVDT4,
  })), [rows]);

  if (!logger) return null;

  if ((logger.data_source || 'sftp') !== 'http') {
    return (
      <Paper variant='outlined' sx={{ p: 3, textAlign: 'center' }}>
        <Typography color='text.secondary'>
          The data stream shows readings a logger sends over HTTP. {logger.name} uploads to the SFTP
          server, so there's nothing to stream. Once it runs an HTTP program, set its Data Source to HTTP.
        </Typography>
      </Paper>
    );
  }

  const pressureUnit = newest ? pressureOf(newest).unit : 'mA';
  const lvdtColors = LVDT_COLORS[theme.palette.mode] || LVDT_COLORS.light;
  const chartDefs = [
    { title: 'Load cell', unit: 'digits', series: [{ key: 'load', name: 'Load cell', color: theme.palette.primary.main }] },
    { title: 'Pressure', unit: pressureUnit, series: [{ key: 'pressure', name: 'Pressure', color: theme.palette.secondary.main }] },
    { title: 'LVDTs', unit: 'mV', series: LVDT_KEYS.map((k, i) => ({ key: k, name: k, color: lvdtColors[i] })) },
  ];
  const anySensor = points.some((p) => ['load', 'pressure', ...LVDT_KEYS].some((k) => p[k] !== null && p[k] !== undefined));

  return (
    <Stack spacing={2}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' rowGap={0.5}>
        <ConnectionBadge connection={connection} />
        <Typography variant='body2' color='text.secondary'>
          Last received {lastReceived === 'Never' ? 'never' : `${lastReceived} ago`}
        </Typography>
      </Stack>

      {!loaded ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {connection === 'live' ? <CircularProgress size={28} /> : (
            <Typography color='text.secondary'>Waiting for the live connection…</Typography>
          )}
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant='outlined' sx={{ p: 3, textAlign: 'center' }}>
          <Typography color='text.secondary'>
            {logger.name} hasn't posted any readings yet. They'll appear here as soon as it does.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Graph first: it's the point of the tab. */}
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Typography variant='caption' color='text.secondary' component='div' sx={{ mb: 1 }}>
              Last 30 minutes of readings (logger clock), newest {clock(Date.parse(newest.Timestamp))}
            </Typography>
            {anySensor ? (
              <Stack spacing={2}>
                {chartDefs.map((c) => <StreamChart key={c.title} points={points} {...c} />)}
              </Stack>
            ) : (
              <Typography color='text.secondary' sx={{ py: 3, textAlign: 'center' }}>
                Every sensor channel reads NAN in this window, so there's nothing to plot. Is anything connected?
              </Typography>
            )}
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1 }}>
            <Stat label='Load cell' value={fmt(newest.Loadcell)} unit='digits' />
            <Stat label='Pressure' value={fmt(pressureOf(newest).value, 2)} unit={pressureUnit} />
            {LVDT_KEYS.map((k) => <Stat key={k} label={k} value={fmt(newest[k], 0)} unit='mV' />)}
            <Stat label='Battery' value={fmt(newest.BattV, 2)} unit='V' />
            <Stat label='Panel temp' value={fmt(newest.PanelTempC)} unit='°C' />
            <Stat label='Job / Point' value={`${newest.JobNumber || '—'} / ${newest.PointNumber || '—'}`} />
            <Stat label='Test' value={newest.TestNumber ?? '—'} />
          </Box>

          <TableContainer component={Paper} variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Time', 'Record', 'Load', 'Pressure', ...LVDT_KEYS, 'Battery'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(-TABLE_ROWS).reverse().map((r) => (
                  <TableRow key={`${r.Timestamp}|${r.RecordNo}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{clock(Date.parse(r.Timestamp))}</TableCell>
                    <TableCell>{r.RecordNo}</TableCell>
                    <TableCell>{fmt(r.Loadcell)}</TableCell>
                    <TableCell>{fmt(pressureOf(r).value, 2)}</TableCell>
                    {LVDT_KEYS.map((k) => <TableCell key={k}>{fmt(r[k], 0)}</TableCell>)}
                    <TableCell>{fmt(r.BattV, 2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
}

export default DataStreamPanel;
